/* eslint-disable react/no-children-prop */
import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import cn from 'classnames';
import { getRequest } from 'utils/requests';
import { env } from 'env';
import {
  Tenant,
  Member,
  countryCodesMap,
  useRightSideBar,
  Box,
  Divider,
  Grid,
  Typography
} from '@p81-common/ui';
import { Loading } from 'components/common/Elements/Elements';
import { getInitialsFull } from 'utils/common';
import {
  ProgressTracking,
  Value,
  GatewayBox
} from './RightSideBarSubComp/index';
import createClasses from './styles';
import { hasAccess, resources, access } from 'utils/permissions';
import { VersionLabelParams } from './types';

const CMLSideBar = (props: any) => {
  const { rowId, url } = props;
  const { t } = useTranslation(['activity']);
  const { handleComponentsArray, setOpenSecondary } = useRightSideBar();
  const [data, setData] = useState<any>();
  const [loader, setLoader] = useState(true);

  useEffect(() => {
    let cancelled = false;
    getRequest(`/cloud/management/logs/sidebar/${rowId}`)
      .then(res => {
        if (!cancelled) {
          setData(res.body);
          setLoader(false);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.log(err);
          setLoader(false);
        }
      });
  }, [rowId]);

  const country = data?.country?.toString();
  //@ts-ignore
  const countryData = countryCodesMap[country];
  const countryFlag = countryData?.flag || 'worldwide';
  const countryFlagSrc = `${env.REACT_APP_STATIC_ASSETS_URL}/flags/${countryFlag}.svg`;

  const styles = createClasses({});
  const hasTenantSensitiveDataReadAccess = hasAccess([
    resources.TENANT_SENSITIVE_DATA,
    access.READ
  ]);

  const getVersionLabel = ({ version, gradual }: VersionLabelParams) => {
    return version
      ? `${version}${
          gradual ? ` - ${t('activity:cloudManagementLogs.GRADUAL')}` : ''
        }`
      : 'N/A';
  };

  if (loader) {
    return <Loading />;
  }

  return (
    <Grid className={styles.innerContainer}>
      <Typography
        className={styles.title}
        children={t('activity:cloudManagementLogs.LOG_DETAILS')}
      />

      <Typography className={styles.midSubTitle}>
        {t('activity:cloudManagementLogs.ABSOLUTE_TIME')}
        <Value
          children={` ${moment(data?.statusHistory?.slice(-1)[0]?.updatedAt)
            .utc(false)
            .format('LLL')}`}
        />
      </Typography>

      <Box mb={3}>
        <Typography
          children={t('activity:cloudManagementLogs.ACTION')}
          className={styles.subTitle}
        />

        <Value className={styles.label}>
          {t('activity:cloudManagementLogs.ACTION')}
          <Value className={styles.actionValue}>{data?.action || 'N/A'}</Value>
        </Value>
        <br />
        {data?.version && (
          <Value className={styles.label}>
            {t('activity:cloudManagementLogs.VERSION')}
            <Value className={cn(styles.actionValue, styles.capitalize)}>
              {getVersionLabel(data)}
            </Value>
          </Value>
        )}
      </Box>
      <Divider className={styles.divider} />

      <Box mb={3}>
        <Typography
          children={t('activity:cloudManagementLogs.UPDATED_BY')}
          className={styles.networkSubTitle}
        />

        <Member
          avatarProps={{
            backgroundColor: '#CB5BCB',
            initials: getInitialsFull(data?.fullName)
          }}
          description={data?.updatedByEmail}
          fullName={data?.fullName}
        />
      </Box>

      <Divider className={styles.divider} />

      <Box mb={3}>
        <Typography
          className={styles.subTitle}
          children={t('activity:cloudManagementLogs.NETWORK')}
        />

        <Typography className={styles.label}>
          {`${t('activity:cloudManagementLogs.NETWORK_ID')}`}
          {data?.networkId ? (
            hasTenantSensitiveDataReadAccess ? (
              <NavLink
                onClick={() => handleComponentsArray()}
                to={`${url}/${data?.customerId}/platform/networks`}
              >
                <Value className={styles.networkId}>{data?.networkId}</Value>
              </NavLink>
            ) : (
              data?.networkId
            )
          ) : (
            <Value>N/A</Value>
          )}
        </Typography>

        <Divider className={styles.divider} />

        <Typography
          children={t('activity:cloudManagementLogs.GATEWAY')}
          className={styles.subTitle}
        />
        {data.entities?.map((gateway: any) => (
          <GatewayBox
            key={gateway.gatewayId}
            {...gateway}
            customerId={data.customerId}
            setOpenSecondary={setOpenSecondary}
          />
        ))}
      </Box>
      <Divider className={styles.divider} />

      <Typography
        children={t('activity:cloudManagementLogs.PROGRESS_TRACKING')}
        className={styles.subTitle}
      />
      <ProgressTracking statusHistory={data?.statusHistory} />

      <Divider className={styles.divider} />

      <Box mb={3}>
        <Typography
          children={t('activity:cloudManagementLogs.COMPANY_NAME')}
          className={styles.networkSubTitle}
        />

        <Tenant
          companyIconProps={{
            badgeProps: {
              badgeContent: (
                <img
                  alt="flag"
                  height="16px"
                  src={countryFlagSrc}
                  width="16px"
                />
              )
            },
            domain: data?.website
          }}
          subTexts={[data?.companySize, data?.customerType]}
          title={data?.companyName}
        />
      </Box>
    </Grid>
  );
};

export default CMLSideBar;
